import { INCOME_CATEGORIES } from "@gladis/shared";
import { Button, Dialog, Label, MoneyInput, SelectField, TextField } from "@gladis/ui";
import { incomeCategoryLabel, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import type { IncomeEntryDto, IncomeSourceDto } from "../dto.js";

/**
 * Record a one-off receipt, or correct one.
 *
 * ==========================================================================================
 * CHOOSING A SOURCE HIDES THE CATEGORY AND THE OWNER, BECAUSE THE SERVER IGNORES THEM
 * ==========================================================================================
 *
 * When an entry is bound to a source, the server COPIES that source's category, ownership and owner
 * onto it and discards whatever the client sent. Showing fields the server will overwrite would be
 * offering a choice that does not exist — so they disappear, and the form says where the values come
 * from instead.
 *
 * That copying is the model rather than defensive coding: an entry records a historical event, so its
 * attribution is the source's AT THE MOMENT IT ARRIVED and must not change when the source is
 * re-attributed next year.
 *
 * CORRECTED IN PLACE, NEVER SUPERSEDED. A bonus typed as €3,400 when it was €340 has no natural
 * successor — appending a corrective entry would leave both in the year total and overstate the
 * household's income by €3,060, permanently.
 */

export interface EntryFormProps {
  open: boolean;
  onClose: () => void;
  editing: IncomeEntryDto | null;
  sources: readonly IncomeSourceDto[];
  onSubmit: (input: {
    incomeSourceId: string | null;
    description: string;
    amount: string;
    receivedOn: string;
    category?: string;
  }) => void;
  pending: boolean;
  error?: string | null;
}

export function EntryForm({
  open,
  onClose,
  editing,
  sources,
  onSubmit,
  pending,
  error,
}: EntryFormProps): ReactNode {
  const t = useT();
  const [sourceId, setSourceId] = useState(editing?.incomeSourceId ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [amount, setAmount] = useState(editing ? String(editing.amountInEuros) : "");
  const [receivedOn, setReceivedOn] = useState(
    editing?.receivedOn ?? new Date().toISOString().slice(0, 10),
  );
  const [category, setCategory] = useState(editing?.category ?? "bonus");

  const bound = sourceId !== "";

  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit({
      incomeSourceId: bound ? sourceId : null,
      description,
      amount,
      receivedOn,
      ...(bound ? {} : { category }),
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t("inc.entry.editHeading") : t("inc.entry.recordHeading")}
      description={t("inc.entry.hint")}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="entry-description">{t("inc.entry.what")}</Label>
          <TextField
            id="entry-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t("inc.entry.whatPlaceholder")}
            required
            maxLength={160}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="entry-amount">{t("inc.source.amount")}</Label>
            <MoneyInput id="entry-amount" value={amount} onValueChange={setAmount} required />
          </div>
          <div>
            <Label htmlFor="entry-date">{t("inc.entry.arrivedOn")}</Label>
            <TextField
              id="entry-date"
              type="date"
              value={receivedOn}
              onChange={(event) => setReceivedOn(event.target.value)}
              required
            />
          </div>
        </div>

        {editing === null ? (
          <div>
            <Label htmlFor="entry-source">{t("inc.entry.partOf")}</Label>
            <SelectField
              id="entry-source"
              value={sourceId}
              onChange={(event) => setSourceId(event.target.value)}
            >
              <option value="">{t("inc.entry.nothingInParticular")}</option>
              {sources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </SelectField>
          </div>
        ) : null}

        {bound ? (
          <p className="text-xs text-muted">{t("inc.entry.inheritsHint")}</p>
        ) : (
          <div>
            <Label htmlFor="entry-category">{t("inc.source.category")}</Label>
            <SelectField
              id="entry-category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              {INCOME_CATEGORIES.map((option) => (
                <option key={option.value} value={option.value}>
                  {incomeCategoryLabel(option.value)}
                </option>
              ))}
            </SelectField>
          </div>
        )}

        {error ? <p className="text-sm text-danger">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={pending}>
            {editing ? t("common.save") : t("inc.history.record")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
